import { useState, useEffect, forwardRef } from "react"
import ReactDatePicker from "react-datepicker"
import { ValidationRules } from "react-hook-form"

import { InputContainer, ModalInput } from "~styled/input"
import colors from "~constants/colors"
import { isArray } from "lodash"

interface DatePickerProps {
  name: string
  label?: string
  infoCopy?: string
  className?: string
  maxLength?: number
  placeholder?: string
  value?: Date | [Date, Date] | string
  errorMessage?: string
  required?: boolean
  setValue: (field: string, val: unknown, config?: Object) => void
  register: (fields: any, rules?: ValidationRules) => void
  getValues: (field: string) => any
  unregister: (field: string) => void
  selectsRange?: boolean
  isClearable?: boolean
  checkDateRange?: (v) => void
}

type CustomInputProps = {
  onClick?: () => void
  value?: string
  onChange?: () => void
}

const DatePicker = (props: DatePickerProps) => {
  const [date, setDate] = useState<Date | null>()
  const [selectedRange, setSelectedRange] = useState([null, null])

  const {
    name,
    label,
    className,
    required = false,
    setValue,
    register,
    getValues,
    unregister,
  } = props

  useEffect(() => {
    register(name, { required })
    if (props.value) {
      onChange(props.value)
    } else {
      const value = getValues(name) || new Date()
      onChange(value)
    }

    return () => unregister(name)
  }, [name, register, unregister])

  const onChange = (date: Date | [Date, Date]) => {
    if (props.selectsRange) {
      setSelectedRange(date)
      setValue(name, date)
      setDate(date[0])
      if (!!props.checkDateRange) {
        if (isArray(date)) {
          props.checkDateRange(date)
        }
      }
    } else {
      setValue(name, date)
      setDate(date)
    }
  }

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ value, onClick, onChange }, ref) => (
      <ModalInput
        color={props.selectsRange ? colors.gray2 : ""}
        onChange={onChange}
        value={value}
        onClick={onClick}
        style={{ height: 38 }}
        ref={ref}
        placeholder={props.placeholder}
      />
    ),
  )

  return (
    <InputContainer className={className} $customMarginBottom={"0px"}>
      {!!label && <span className="label">{label}</span>}

      <ReactDatePicker
        name={name}
        selected={date}
        {...{ onChange }}
        customInput={<CustomInput />}
        selectsStart={true}
        selectsEnd={true}
        startDate={selectedRange[0]}
        endDate={selectedRange[1]}
        selectsRange={props.selectsRange}
        isClearable={props.isClearable || false}
      />

      <span className={"error-message"}>{props.errorMessage}</span>
    </InputContainer>
  )
}

export default DatePicker
